import React, { useEffect, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { getAliceRecommendedAttributes, importAttributeInEntity } from '../../../API/AliceGPT/AliceGPT';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import { convertTypeToDesignerType } from '../../Helpers/Recommendation/AliceGPT';
import { getDatalakeImageUrl } from '../../Helpers/DateLakeStorage';
import EditPicklistDialog from '../../Dashboard/Project/AliceGPT/EditPicklistDialog';
import FailedToLoadError from '../../Shared/Recommendation/Error/FailedToLoadError';
import AttributeTypeDropdown, { recommendationAttributeType } from '../../Shared/Recommendation/AttributeTypeDropdown';
import { useTranslation } from 'react-i18next';
import InnerLoader from '../../Shared/Loader/InnerLoader';
import { getEntities } from '../../../API/Entity/Entity';
import { OPENAI_MODEL } from '../../../Constants/OpenAI';

function AliceAttributeRecommendationDialog({
    organization, project, entityName, entityId, existingAttributeNames, hasStateAttribute, domain, description,
    toggleDialog, onAttributesImported
}) {

    const { t } = useTranslation([
        "attribute/recommendation_dialog",
        "common/attribute_type_dropdown"
    ], { useSuspense: false });

    const numStateAttributesAllowedToSelect = hasStateAttribute ? 0 : 1;
    const [loading, setLoading] = useState(true);
    const [recommendedAttributes, setRecommendedAttributes] = useState([]);
    const [picklistAttributeToEdit, setPicklistAttributeToEdit] = useState(null);
    const [creatingAttribute, setCreatingAttribute] = useState(false);
    const [entities, setEntities] = useState([]);
    const [editAttributeTypeIndex, setEditAttributeTypeIndex] = useState(-1);

    const [error, setError] = useState("");

    useEffect(() => {
        getAttributes();
    }, [])

    async function getAttributes() {
        try {
            const entities = await getEntities(project._id);
            const attributes = await getAliceRecommendedAttributes(
                entityName, OPENAI_MODEL, existingAttributeNames, domain, description,
                project.language
            )
            const filteredAttributes = attributes.filter(attribute => !existingAttributeNames.includes(attribute[0]));
            const attributesForDrawer = filteredAttributes.map(attributesToStateArrayElement);
            const allEntityNames = entities.map(entity => entity.name);
            const attributesWithoutEntityNames = removeAttributeWithOtherEntityName(attributesForDrawer, allEntityNames);
            setRecommendedAttributes(attributesWithoutEntityNames);
        } catch (error) {
            setError(error.message);
        }
        setLoading(false);
    }

    function attributesToStateArrayElement(attribute) {
        return {
            name: attribute[0],
            type: convertTypeToDesignerType(attribute[1]),
            picklist: attribute[2],
            unique: attribute[3],
            checked: false,
            selected: false
        }
    }

    function removeAttributeWithOtherEntityName(attributes, entityNames) {
        const newAttributes = attributes.filter((attribute) => {
            return !entityNames.some(name => (
                name !== entityName &&
                attribute.name.toLowerCase().includes(name.toLowerCase())
            ))
        });
        return newAttributes;
    }

    function handleToggle() {
        toggleDialog();
    }

    function toggleSelectAttribute(attributeIndex) {
        const newAttributes = [...recommendedAttributes];
        newAttributes[attributeIndex].checked = !newAttributes[attributeIndex].checked;
        setRecommendedAttributes(newAttributes);
    }

    function handleEditPicklistClick(attribute) {
        setPicklistAttributeToEdit(attribute);
    }

    async function handlePicklistUpdate(picklist) {
        setRecommendedAttributes(recommendedAttributes.map(attribute => {
            if (attribute.name === picklistAttributeToEdit.name) {
                return {
                    ...attribute,
                    picklist
                }
            }
            return attribute;
        }));
        setPicklistAttributeToEdit(null);
    }

    function toggleSelectAllAttributes() {
        const allAttributesSelected = recommendedAttributes.every(attribute => attribute.checked);
        setRecommendedAttributes(
            prevValue => prevValue.map(attribute => ({
                ...attribute,
                checked: !allAttributesSelected
            }))
        )
    }

    async function handleSubmit() {
        try {
            setCreatingAttribute(true);
            const attributes = recommendedAttributes.filter(attribute => attribute.checked).map(attribute => {
                let picklist = attribute.picklist;
                if (attribute.type === "State Field" && picklist.length === 0) {
                    picklist = ["Not Started", "In Progress", "Completed"];
                }
                return {
                    name: attribute.name?.trim(),
                    type: attribute.type,
                    picklist: picklist,
                    required: attribute.type === "State Field" ? true : attribute.required,
                    unique: attribute.unique
                }
            });
            const response = await importAttributeInEntity(organization.subscriptionId, project._id, entityId, attributes);
            onAttributesImported(response);
        } catch (e) {
            console.log(e);
        } finally {
            handleToggle();
            setCreatingAttribute(false);
        }
    }

    function handleAttributeTypeClick(event, index) {
        setEditAttributeTypeIndex(index);
    }

    function handleAttributeTypeChange(event, index) {
        const attribute = recommendedAttributes[index];
        setRecommendedAttributes(
            prevValue => prevValue.map((attribute, i) => {
                if (i === index) {
                    return {
                        ...attribute,
                        type: event.target.value.value,
                        picklist: attribute.picklist ? attribute.picklist : ["Value 1"]
                    }
                }
                return attribute;
            })
        )
        clearEditAttributeIndex()
    }

    function clearEditAttributeIndex() {
        setEditAttributeTypeIndex(-1);
    }

    function getAttributeType(attributeType) {
        const type = recommendationAttributeType.find((item) => item.value === attributeType);
        return type ? t(type.value, { ns: "common/attribute_type_dropdown" }) : "";
    }

    function disableSave() {
        return (
            recommendedAttributes.filter(attribute => attribute.checked).length === 0 ||
            recommendedAttributes.filter(
                attribute => attribute.checked && attribute.type === "State Field"
            ).length > numStateAttributesAllowedToSelect ||
            recommendedAttributes.some(attribute =>
                (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                (!attribute.picklist || attribute.picklist.length === 0) &&
                attribute.checked
            ) ||
            creatingAttribute
        )
    }

    return (
        <>
            {
                picklistAttributeToEdit &&
                <EditPicklistDialog
                    title={picklistAttributeToEdit.type === "Picklist String" ? t("edit_picklist_dialog.edit_picklist_label") : t("edit_picklist_dialog.edit_state_label")}
                    addOptionText={picklistAttributeToEdit.type === "Picklist String" ? t("edit_picklist_dialog.add_picklist_label") : t("edit_picklist_dialog.add_state_label")}
                    attribute={picklistAttributeToEdit}
                    onCancel={() => handleEditPicklistClick(null)}
                    onClose={() => handleEditPicklistClick(null)}
                    onSubmit={handlePicklistUpdate} />
            }
            <Dialog
                title={t("title")}
                height={"80%"}
                width={"70%"}
                onClose={handleToggle}
            >
                {
                    loading &&
                    <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100">
                        <img src={getDatalakeImageUrl('/loaders/RecommendationLoader.gif')} alt="" />
                        <h3>{t("loading_message")}</h3>
                    </div>
                }
                {
                    !loading &&
                    error.length > 0 &&
                    <FailedToLoadError />
                }
                {
                    !loading &&
                    error.length === 0 &&
                    <Grid
                        data={
                            recommendedAttributes.map((attribute, index) => ({
                                ...attribute,
                                _id: index + 1
                            }))
                        }
                        rowHeight={46}
                    >
                        <GridColumn
                            width={17}
                            title={' '}
                            field='_id' />
                        <GridColumn
                            width={16}
                            headerClassName='p-0'
                            headerCell={() => (
                                <Checkbox
                                    checked={recommendedAttributes.every(attribute => attribute.checked)}
                                    value={recommendedAttributes.every(attribute => attribute.checked)}
                                    onChange={toggleSelectAllAttributes}
                                    className='mt-0'
                                    style={{ marginBottom: '7px', marginLeft: '6px', marginTop: "0px" }}
                                />
                            )}
                            cell={({ dataIndex, dataItem }) => (
                                <td>
                                    <Checkbox
                                        checked={dataItem.checked}
                                        value={dataItem.checked}
                                        onChange={() => toggleSelectAttribute(dataIndex)}
                                    />
                                </td>
                            )}
                        />
                        <GridColumn
                            width={100}
                            title={t("grid.name_column_label")}
                            field='name'
                        />
                        <GridColumn
                            title={t("grid.type_column_label")}
                            field='type'
                            width={200}
                            cell={({ dataIndex: index, dataItem }) => (
                                <td>
                                    {
                                        editAttributeTypeIndex === index ?
                                            <div className='d-flex'>
                                                <div className='w-50'>
                                                    <AttributeTypeDropdown
                                                        value={dataItem.type}
                                                        onChange={(event) => handleAttributeTypeChange(event, index)} />
                                                </div>
                                                &nbsp;&nbsp;&nbsp;
                                                <button className="btn btn-link m-0 p-0 text-danger" onClick={clearEditAttributeIndex}>
                                                    <i className="fa-solid fa-close" />
                                                </button>
                                            </div>
                                            :
                                            <>
                                                <button className="btn btn-link m-0 p-0" onClick={(e) => handleAttributeTypeClick(e, index)}>
                                                    {getAttributeType(dataItem.type)}
                                                </button>
                                                {
                                                    (dataItem.type === 'Picklist String' || dataItem.type === "State Field")
                                                    &&
                                                    <button
                                                        className='btn btn-link'
                                                        onClick={() => handleEditPicklistClick(dataItem)}
                                                    >
                                                        <i className='fa-solid fa-edit' />
                                                    </button>
                                                }
                                                {
                                                    (dataItem.type === 'Picklist String' || dataItem.type === "State Field")
                                                    &&
                                                    <span>{dataItem.picklist?.slice(0, 3).join(', ')}</span>
                                                }
                                                {
                                                    (dataItem.type === 'Picklist String' || dataItem.type === "State Field") &&
                                                    (!dataItem.picklist || dataItem.picklist.length === 0) &&
                                                    <span className='text-danger'>
                                                        {
                                                            dataItem.type === 'Picklist String' ?
                                                                t("errors.no_picklist_error") :
                                                                t("errors.no_state_error")
                                                        }
                                                    </span>
                                                }
                                            </>
                                    }
                                </td>
                            )} />
                    </Grid>
                }
                {
                    !loading &&
                    <DialogActionsBar layout='end'>
                        <div className='d-flex align-items-center mb-4 me-3 pe-2'>
                            {
                                recommendedAttributes.filter(attribute => attribute.checked && attribute.type === "State Field").length > numStateAttributesAllowedToSelect &&
                                <span className='text-danger'>
                                    {t("errors.one_state_field_allowed")}
                                </span>
                            }
                            {
                                creatingAttribute &&
                                <InnerLoader borderColor="green" />
                            }
                            <button className='btn' onClick={handleToggle}>
                                {t("buttons.cancel_label")}
                            </button>
                            <button
                                className='btn btn-primary'
                                onClick={handleSubmit}
                                disabled={disableSave()}
                            >
                                {t("buttons.add_label")}
                            </button>
                        </div>
                    </DialogActionsBar>
                }
            </Dialog>
        </>
    )
}

export default AliceAttributeRecommendationDialog;